import { useParams } from '@tanstack/react-router';
import { Button, Illustration, notify, PasswordInput, YemboLogo } from '@yembo/yemblocks-core';
import { isValidEmail } from '@yembo/yemblocks-core/dist/_util';
import { TextInput } from '@yembo/yemblocks-core/dist/TextInput';
import { MouseEvent, useState } from 'react';
import { putResetPassword } from 'src/queries/Login';
import { removeAuth } from 'src/util/AuthModule';
import { useDocumentTitle } from 'src/react/_hooks/useDocumentTitle';
import { useSubmitOnEnterKey } from 'src/react/_hooks/useEnterKeySubmit';
import { PrivacyNotes } from 'src/react/_shared/PrivacyNotes';
import { SectionHeader } from 'src/react/Authentication/LoginMfa/SectionHeader';
import { router } from 'src/router';
import { isProdGovEnvironment } from 'src/util/env';
import { getPasswordRequirements } from 'src/util/getPasswordRequirements';
import { RequirementList } from '../../../_shared/RequirementList';
import { ImportanceNotice } from '../elements/ImportanceNotice';

const initialState = {
  email: '',
  isEmailValid: false,
  password: '',
  confirmPassword: '',
};

export const ResetPassword = () => {
  useDocumentTitle('Reset Password');

  // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
  const { token } = useParams({ strict: false }) as any;

  // eslint-disable-next-line react-hooks/immutability
  if (!token) window.location.href = '/';

  const [isNoticeSubmitted, setIsNoticeSubmitted] = useState(false);
  const [state, setState] = useState(initialState);
  const [isResetInProcess, setIsResetInProcess] = useState(false);

  const { email, isEmailValid, password, confirmPassword } = state;

  // Derived on every render rather than kept from the password input's own
  // validity: "not contain your email" depends on the email, which can be
  // entered after the password.
  const { requirements, isPasswordValid } = getPasswordRequirements({ password, email });

  const canSubmit = isEmailValid && isPasswordValid && password === confirmPassword;

  const onSubmit = async (event: MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
    event.preventDefault();

    if (!canSubmit || isResetInProcess) return;

    setIsResetInProcess(true);

    try {
      const resetData = {
        email,
        password,
        passwordResetToken: token ?? '',
      };
      await putResetPassword(resetData);

      // A reset ends every session the employee had, this browser's included,
      // so drop the stored one and let the redirect land on the sign-in screen.
      localStorage.clear();
      removeAuth();

      notify.success('Your password has been updated. Redirecting...');
      router.navigate({ to: '/' });
    } finally {
      setIsResetInProcess(false);
    }
  };

  useSubmitOnEnterKey({ onSubmit });

  return (
    <div className='reset-password authentication'>
      <div className='card-area'>
        <div className='card'>
          <YemboLogo />
          <div className='illustration-wrapper'>
            <Illustration type='cat-sleeping' className='illustration' />
          </div>
          <div className='reset-password-form authentication-form'>
            {!isNoticeSubmitted && isProdGovEnvironment ? (
              <ImportanceNotice onSubmit={() => setIsNoticeSubmitted(true)} />
            ) : (
              <>
                <SectionHeader
                  heading='Reset Password'
                  caption='Enter the email you sign in with and your new password.'
                />
                <div className='password-inputs'>
                  <div className='email-wrapper'>
                    <TextInput
                      name='email'
                      inputMode='email'
                      autoComplete='username'
                      label='Email'
                      value={email}
                      onChange={({ currentTarget: { value } }) => {
                        value = value.replaceAll(' ', '');
                        setState((prevState) => ({
                          ...prevState,
                          email: value,
                          isEmailValid: isValidEmail(value),
                        }));
                      }}
                      checkValidity={({ value }) => (!value || isValidEmail(value) ? undefined : 'Invalid email')}
                    />
                  </div>
                  <div className='password-wrapper'>
                    <PasswordInput
                      label='Password'
                      onChange={({ currentTarget: { value } }) => {
                        setState((prevState) => ({
                          ...prevState,
                          password: value,
                        }));
                      }}
                      // Until there is an email to compare with, leave the verdict to the requirement list.
                      checkValidity={({ value }) =>
                        !value || !email || getPasswordRequirements({ password: value, email }).isPasswordValid
                          ? undefined
                          : 'Password is not secure enough'
                      }
                    />
                  </div>
                  <div className='password-wrapper'>
                    <PasswordInput
                      label='Confirm Password'
                      onChange={({ currentTarget: { value } }) =>
                        setState((prevState) => ({
                          ...prevState,
                          confirmPassword: value,
                        }))
                      }
                      checkValidity={({ value }) =>
                        !value || value === password ? undefined : 'Passwords do not match'
                      }
                    />
                  </div>
                </div>
                <RequirementList requirements={requirements} />
                <Button text='Submit' isLoading={isResetInProcess} isDisabled={!canSubmit} onClick={onSubmit} />
              </>
            )}
          </div>
        </div>
      </div>
      <div className='auth-footer'>
        <PrivacyNotes />
      </div>
    </div>
  );
};
